/**
 * ActiveBranchChip — persistent TopBar affordance (CONFIG-03).
 *
 * Renders the user's "active branch" — primary by default, today's override
 * when one exists. Always visible in the TopBar so the officer knows where
 * they're booking work to.
 *
 * Visual states (per design §5.3, adapted for v3 brand tokens — Bill
 * 2026-04-24: streamline + fit our v3 UI/UX, don't carbon-copy SPFx):
 *   - Default  : neutral chip, e.g. "Branch 01 · Downtown Rolla"
 *   - Override : accent-outlined chip, "Working from 02 · Highway 72 today"
 *                with a small PCB-green dot indicator. Hover surfaces a `×`
 *                that triggers DELETE → reset-to-primary.
 *
 * Click → opens BranchOverrideModal (set/replace today's override).
 */
import { useState } from 'react';
import { ChevronDown, X } from '@retail-ops/panel-shell/fi';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useConfig } from '@/contexts/ConfigContext';
import { BranchOverrideModal } from './BranchOverrideModal';
import './ActiveBranchChip.css';

export function ActiveBranchChip(): JSX.Element | null {
  const { user, refreshBranch } = useAuth();
  const { config } = useConfig();
  const client = useApiClient();
  const [open, setOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  // No active branch resolved yet → render nothing. AuthContext handles
  // the loading + 404-fallback path; if neither is populated, the chip is
  // simply hidden until the next bootstrap.
  const activeCode = user?.activeBranchCode ?? user?.branchCode;
  if (!activeCode) return null;

  const overrideActive = user?.overrideActive === true;
  // NAME FALLBACK (Bill 2026-09-11). user.activeBranchName is populated only
  // when the branch-context bootstrap returns it, and in production it did
  // not - so this chip rendered a bare "Branch 01" while PopOutGate, which
  // resolves the same code through the branch catalog, rendered "Branch 01 -
  // Rolla Downtown". Same lookup here, so the two agree.
  const fromCatalog = (code: string | undefined): string | undefined =>
    code ? config?.branches?.find((b) => b.branchCode === code)?.branchName : undefined;
  const activeName = user?.activeBranchName ?? fromCatalog(activeCode);
  const primaryCode = user?.primaryBranchCode;
  const primaryName = user?.primaryBranchName ?? fromCatalog(primaryCode);

  const label = overrideActive
    ? `Working from ${activeCode}${activeName ? ' · ' + activeName : ''} today`
    : `Branch ${activeCode}${activeName ? ' · ' + activeName : (primaryName ? ' · ' + primaryName : '')}`;

  const handleReset = async (): Promise<void> => {
    if (resetting) return;
    setResetting(true);
    try {
      await client.retail.session.clearBranchOverride();
      await refreshBranch();
    } catch {
      // Silent — design §6.4 says don't block on failure. The chip will
      // re-sync on the next refreshBranch / bootstrap.
    } finally {
      setResetting(false);
    }
  };

  return (
    <>
      <div
        className={
          'active-branch-chip' +
          (overrideActive ? ' active-branch-chip--override' : '') +
          (resetting ? ' active-branch-chip--loading' : '')
        }
        role="group" data-tour="topbar-branch"
        aria-label="Active branch"
      >
        <button
          type="button"
          className="active-branch-chip__open"
          onClick={() => setOpen(true)}
          // The phone shows only "Br 01", so the full name rides on the accessible name.
          aria-label={`${label}. Change branch for today`}
          title={
            overrideActive && primaryCode
              ? `Working from ${activeCode}${activeName ? ' — ' + activeName : ''} today (home: ${primaryCode}${primaryName ? ' — ' + primaryName : ''})`
              : 'Change branch for today'
          }
        >
          {overrideActive && (
            <span className="active-branch-chip__dot" aria-hidden />
          )}
          {/* Full label on desktop; compact "Br 01" on a phone (<= 640px, see ActiveBranchChip.css). */}
          <span className="active-branch-chip__label" aria-hidden>{label}</span>
          <span className="active-branch-chip__short" aria-hidden>Br {activeCode}</span>
          <ChevronDown
            size={11}
            aria-hidden
            className="active-branch-chip__caret"
          />
        </button>
        {overrideActive && (
          <button
            type="button"
            className="active-branch-chip__reset"
            onClick={handleReset}
            aria-label="Reset to primary branch"
            title="Reset to primary branch"
            disabled={resetting}
          >
            <X size={12} aria-hidden />
          </button>
        )}
      </div>

      <BranchOverrideModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
